import { SphereXGAnnotationData } from "@/components/ui/annotations/annotation-props";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { setHasCreatedNewAnnotation } from "@/store/create-annotation-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectTags } from "@/store/tags/tags-selectors";
import { setAnnotationExpansion } from "@/store/ui/ui-slice";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import { PointAnnotationData } from "@/tools/annotation-mutation-utils";
import {
  CreateSphereSinglePointAnnotationParams,
  useCreateSphereSinglePointAnnotations,
} from "@/tools/annotation-picker/use-create-single-point-annotation";
import {
  GUID,
  IElement,
  IElementTypeHint,
  isIElementGenericImgSheet,
} from "@faro-lotv/ielement-types";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { Vector3Tuple } from "three";
import { AnnotationDescription } from "./import-annotations-parse";

type CreateAnnotationsOutput = {
  // Number of annotations successfully created
  numberAnnotationsCreated: number;

  // Error messages (if any)
  errorMessages: string[];
};

/**
 * @param iElement The IElement to which the annotations will be attached
 * @param annotations The list of descriptions of annotations to create
 * @param updateProgress A callback function for reporting progress (0=starting to 100=completed)
 * @returns a promise with output of the creation (see CreateAnnotationsOutput)
 */
export function useCreateAnnotations(): (
  iElement: IElement,
  annotations: AnnotationDescription[],
  updateProgress: (progress: number) => void,
) => Promise<CreateAnnotationsOutput> {
  const dispatch = useAppDispatch();

  const createSinglePointAnnotations = useCreateSphereSinglePointAnnotations();

  const projectTags = useAppSelector(selectTags);
  const { handleErrorWithToast } = useErrorHandlers();

  /**
   * Create all annotations described in input list.
   *
   * @param iElement - The IElement to which the annotations belong.
   * @param annotations - The list of annotations to create.
   * @returns The number of annotations successfully created.
   */
  return useCallback(
    async (
      iElement: IElement,
      annotations: AnnotationDescription[],
    ): Promise<CreateAnnotationsOutput> => {
      const output: CreateAnnotationsOutput = {
        numberAnnotationsCreated: 0,
        errorMessages: [],
      };
      const annotationOnSheet = isIElementGenericImgSheet(iElement);
      const annotationsParams: CreateSphereSinglePointAnnotationParams[] = [];
      for (const [annotationIndex, annotation] of annotations.entries()) {
        // Extract coordinates (apply conversion from user/frontend to backend)
        const pickedPoint: Vector3Tuple = [
          Number(annotation.GlobalPoseX),
          Number(annotation.GlobalPoseZ),
          -Number(annotation.GlobalPoseY),
        ];
        if (
          Number.isNaN(pickedPoint[0]) ||
          Number.isNaN(pickedPoint[1]) ||
          Number.isNaN(pickedPoint[2])
        ) {
          // Invalid coordinates
          output.errorMessages.push(
            `Annotation #${annotationIndex}: invalid coordinates: "${pickedPoint[0]}, ${pickedPoint[1]}, ${pickedPoint[2]}"`,
          );
          continue;
        }

        // Handle Due Date
        const dueDateInput = annotation["Due Date"];
        const dueDateMaybeInvalid = dueDateInput
          ? DateTime.fromISO(dueDateInput)
          : undefined;
        if (dueDateInput && !dueDateMaybeInvalid?.isValid) {
          // Invalid due date
          output.errorMessages.push(
            `Annotation #${annotationIndex}: Invalid due date: ${dueDateInput}`,
          );
          continue;
        }
        const dueDate = dueDateMaybeInvalid?.isValid
          ? dueDateMaybeInvalid.toJSDate()
          : undefined;

        // Handle tags
        const tagsNames = annotation.Tags
          ? annotation.Tags.split(",").map((tag) => tag.trim())
          : [];
        const tags = tagsNames.flatMap(
          (name) => projectTags.find((tag) => tag.name === name) ?? [],
        );
        if (tags.length !== tagsNames.length) {
          // Some tags were not found
          output.errorMessages.push(
            `Annotation #${annotationIndex}: Some tags do not exist in this project: ${tagsNames.filter(
              (name) => !projectTags.find((tag) => tag.name === name),
            )}`,
          );
          continue;
        }

        const annotationData: PointAnnotationData = {
          points: [pickedPoint],
          parentId: iElement.id,
          type: annotationOnSheet
            ? IElementTypeHint.mapAnnotation
            : IElementTypeHint.spaceAnnotation,
        };
        const statusFromCsv = annotation.Status;
        const details: SphereXGAnnotationData = {
          title: annotation.Name,
          assignee: annotation.Assignee,
          description: annotation.Description,
          dueDate,
          status: statusFromCsv === "Unclassified" ? undefined : statusFromCsv,
          newAttachments: [],
          tags,
        };

        annotationsParams.push({
          annotation: annotationData,
          details,
          cameraParams: undefined,
        });
      }

      let newMarkups: GUID[] = new Array<GUID>();
      try {
        newMarkups = await createSinglePointAnnotations(annotationsParams);
      } catch (error) {
        handleErrorWithToast({
          title: "Failed to import annotations",
          error,
        });
        output.errorMessages.push("Failed to create annotations.");
        console.error(error);
        return output;
      }

      output.numberAnnotationsCreated = newMarkups.length;

      // Auto expand the new annotation
      for (const markupId of newMarkups) {
        dispatch(setAnnotationExpansion({ id: markupId, expanded: true }));
      }

      dispatch(
        setObjectVisibility({
          type: ViewObjectTypes.annotations,
          visibility: true,
        }),
      );

      dispatch(setHasCreatedNewAnnotation(true));

      return output;
    },
    [createSinglePointAnnotations, dispatch, projectTags, handleErrorWithToast],
  );
}
